import {useId, useRef} from "react";
import {css, cx} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {CheckIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {Text} from "@pg-design/text-module";
import {useElementWasVisible} from "@pg-mono/hooks";
import {IConsultant as ILeadModalConsultant} from "@pg-rp/consultants";

import {gtmHotlineConsultantWidgetClick, gtmHotlineConsultantWidgetView} from "../tracking/gtm_hotline_consultant_widget";

type HotlineConsultantWidgetVariant = "floating" | "delayed";

const widgetBenefits = [
    {
        strongText: "Bezpłatnie",
        text: " wybiorę dla Ciebie najlepsze mieszkania."
    },
    {
        strongText: "Oszczędzę Twój czas",
        text: ", odrzucając nietrafione ogłoszenia."
    },
    {
        strongText: "Pomogę Ci",
        text: " w całym procesie zakupowym."
    }
];

interface IProps {
    consultant: ILeadModalConsultant;
    primaryCtaLabel: string;
    variant: HotlineConsultantWidgetVariant;
    onPrimaryCtaClick: () => void;
    primaryCtaDataTestId?: string;
    trackingEventCategory?: string;
}

export const HotlineConsultantWidget = (props: IProps) => {
    const widgetTrackingElementId = useId();
    const hasTrackedViewRef = useRef(false);
    const trackingDisabled = !props.trackingEventCategory;

    useElementWasVisible({
        ids: trackingDisabled ? [] : [widgetTrackingElementId],
        onElementVisible: () => {
            if (!props.trackingEventCategory || hasTrackedViewRef.current) {
                return;
            }

            hasTrackedViewRef.current = true;
            gtmHotlineConsultantWidgetView({eventCategory: props.trackingEventCategory});
        },
        once: true,
        threshold: 0.05,
        disabled: trackingDisabled
    });

    const trackClick = (eventLabel: string) => {
        if (!props.trackingEventCategory) {
            return;
        }

        gtmHotlineConsultantWidgetClick({eventCategory: props.trackingEventCategory, eventLabel: eventLabel.replace(/\s+/g, " ").trim()});
    };

    const handlePrimaryCtaClick = () => {
        trackClick(props.primaryCtaLabel);
        props.onPrimaryCtaClick();
    };

    return (
        <div className={cx(widgetStyle, variantStyle[props.variant])} data-id={widgetTrackingElementId}>
            <Image
                width="128"
                height="128"
                src={props.consultant.image.s144}
                src2x={props.consultant.image.s216}
                alt={props.consultant.fullName}
                className={consultantImageStyle}
            />

            <div className={consultantInfoStyle}>
                <Text variant="headline_4" as="p" align="center" className={consultantNameStyle}>
                    {props.consultant.fullName}
                </Text>
                <Text variant="info_txt_1" as="p" align="center" color="var(--colors-gray-800)" className={consultantRoleStyle}>
                    Konsultant mieszkaniowy
                </Text>
            </div>

            <Text variant="body_copy_2" as="p" className={descriptionStyle}>
                Zostaw kontakt, a doradca przygotuje dla Ciebie zestawienie mieszkań.
            </Text>

            <ul className={benefitsListStyle}>
                {widgetBenefits.map((benefit) => (
                    <li key={benefit.strongText} className={benefitItemStyle}>
                        <CheckIcon size="1.5" wrapperSize="1.6" wrapperColor="var(--colors-success)" className={benefitIconStyle} />
                        <Text variant="body_copy_2" as="span" className={benefitTextStyle}>
                            <strong>{benefit.strongText}</strong>
                            {benefit.text}
                        </Text>
                    </li>
                ))}
            </ul>

            <div className={contactActionsStyle}>
                <Button
                    variant="filled_primary"
                    type="button"
                    size="medium"
                    onClick={handlePrimaryCtaClick}
                    className={primaryCtaStyle}
                    dataTestId={props.primaryCtaDataTestId}
                >
                    {props.primaryCtaLabel}
                </Button>
            </div>
        </div>
    );
};

const widgetStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    color: "var(--colors-secondary)",
    textAlign: "center"
});

const variantStyle: Record<HotlineConsultantWidgetVariant, string> = {
    floating: css({
        pt: 1.5,
        px: 0,
        pb: 0
    }),
    delayed: css({
        pt: 3,
        px: 0,
        pb: 0,

        md: {
            pt: 0
        }
    })
};

const consultantImageStyle = css({
    width: "11.2rem",
    height: "11.2rem",
    borderRadius: "50%",
    border: "0.6rem solid var(--colors-primary)",
    boxShadow: "var(--shadows-1)",
    mb: 2,

    md: {
        width: "8.8rem",
        height: "8.8rem",
        borderWidth: "0.5rem",
        mb: 2
    },

    "& img": {
        width: "100%",
        height: "100%",
        objectFit: "cover"
    }
});

const consultantInfoStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 0.25,
    mb: 4
});

const consultantNameStyle = css({
    m: 0
});

const consultantRoleStyle = css({
    m: 0
});

const descriptionStyle = css({
    m: 0,
    maxWidth: "48rem",
    color: "var(--colors-secondary)",
    textAlign: "left",
    mb: 2,

    md: {
        maxWidth: "44rem"
    }
});

const benefitsListStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2.5,
    width: "100%",
    maxWidth: "48rem",
    m: 0,
    p: 0,
    listStyle: "none",
    color: "var(--colors-secondary)",
    textAlign: "left",
    mb: 4,

    md: {
        maxWidth: "44rem",
        gap: 2
    }
});

const benefitItemStyle = css({
    display: "grid",
    gridTemplateColumns: "1.6rem minmax(0, 1fr)",
    gap: 1,
    alignItems: "start"
});

const benefitIconStyle = css({
    mt: "2px",
    color: "var(--colors-secondary)"
});

const benefitTextStyle = css({
    lineHeight: 1.35
});

const contactActionsStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    gap: 1.5
});

const primaryCtaStyle = css({
    width: "100%",
    minWidth: "20rem",
    maxWidth: "46.8rem",
    textTransform: "uppercase"
});
import {useId, useRef} from "react";
import {css, cx} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {CheckIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {Text} from "@pg-design/text-module";
import {useElementWasVisible} from "@pg-mono/hooks";
import {IConsultant as ILeadModalConsultant} from "@pg-rp/consultants";

import {gtmHotlineConsultantWidgetClick, gtmHotlineConsultantWidgetView} from "../tracking/gtm_hotline_consultant_widget";

type HotlineConsultantWidgetVariant = "floating" | "delayed";

const widgetBenefits = [
    {
        strongText: "Bezpłatnie",
        text: " wybiorę dla Ciebie najlepsze mieszkania."
    },
    {
        strongText: "Oszczędzę Twój czas",
        text: ", odrzucając nietrafione ogłoszenia."
    },
    {
        strongText: "Pomogę Ci",
        text: " w całym procesie zakupowym."
    }
];

interface IProps {
    consultant: ILeadModalConsultant;
    primaryCtaLabel: string;
    variant: HotlineConsultantWidgetVariant;
    onPrimaryCtaClick: () => void;
    primaryCtaDataTestId?: string;
    trackingEventCategory?: string;
}

export const HotlineConsultantWidget = (props: IProps) => {
    const widgetTrackingElementId = useId();
    const hasTrackedViewRef = useRef(false);
    const trackingDisabled = !props.trackingEventCategory;

    useElementWasVisible({
        ids: trackingDisabled ? [] : [widgetTrackingElementId],
        onElementVisible: () => {
            if (!props.trackingEventCategory || hasTrackedViewRef.current) {
                return;
            }

            hasTrackedViewRef.current = true;
            gtmHotlineConsultantWidgetView({eventCategory: props.trackingEventCategory});
        },
        once: true,
        threshold: 0.05,
        disabled: trackingDisabled
    });

    const trackClick = (eventLabel: string) => {
        if (!props.trackingEventCategory) {
            return;
        }

        gtmHotlineConsultantWidgetClick({eventCategory: props.trackingEventCategory, eventLabel: eventLabel.replace(/\s+/g, " ").trim()});
    };

    const handlePrimaryCtaClick = () => {
        trackClick(props.primaryCtaLabel);
        props.onPrimaryCtaClick();
    };

    return (
        <div className={cx(widgetStyle, variantStyle[props.variant])} data-id={widgetTrackingElementId}>
            <Image
                width="128"
                height="128"
                src={props.consultant.image.s144}
                src2x={props.consultant.image.s216}
                alt={props.consultant.fullName}
                className={consultantImageStyle}
            />

            <div className={consultantInfoStyle}>
                <Text variant="headline_4" as="p" align="center" className={consultantNameStyle}>
                    {props.consultant.fullName}
                </Text>
                <Text variant="info_txt_1" as="p" align="center" color="var(--colors-gray-800)" className={consultantRoleStyle}>
                    Konsultant mieszkaniowy
                </Text>
            </div>

            <Text variant="body_copy_2" as="p" className={descriptionStyle}>
                Zostaw kontakt, a doradca przygotuje dla Ciebie zestawienie mieszkań.
            </Text>

            <ul className={benefitsListStyle}>
                {widgetBenefits.map((benefit) => (
                    <li key={benefit.strongText} className={benefitItemStyle}>
                        <CheckIcon size="1.5" wrapperSize="1.6" wrapperColor="var(--colors-success)" className={benefitIconStyle} />
                        <Text variant="body_copy_2" as="span" className={benefitTextStyle}>
                            <strong>{benefit.strongText}</strong>
                            {benefit.text}
                        </Text>
                    </li>
                ))}
            </ul>

            <div className={contactActionsStyle}>
                <Button
                    variant="filled_primary"
                    type="button"
                    size="medium"
                    onClick={handlePrimaryCtaClick}
                    className={primaryCtaStyle}
                    dataTestId={props.primaryCtaDataTestId}
                >
                    {props.primaryCtaLabel}
                </Button>
            </div>
        </div>
    );
};

const widgetStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    color: "var(--colors-secondary)",
    textAlign: "center"
});

const variantStyle: Record<HotlineConsultantWidgetVariant, string> = {
    floating: css({
        pt: 1.5,
        px: 0,
        pb: 0
    }),
    delayed: css({
        pt: 3,
        px: 0,
        pb: 0,

        md: {
            pt: 0
        }
    })
};

const consultantImageStyle = css({
    width: "11.2rem",
    height: "11.2rem",
    borderRadius: "50%",
    border: "0.6rem solid var(--colors-primary)",
    boxShadow: "var(--shadows-1)",
    mb: 2,

    md: {
        width: "8.8rem",
        height: "8.8rem",
        borderWidth: "0.5rem",
        mb: 2
    },

    "& img": {
        width: "100%",
        height: "100%",
        objectFit: "cover"
    }
});

const consultantInfoStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 0.25,
    mb: 4
});

const consultantNameStyle = css({
    m: 0
});

const consultantRoleStyle = css({
    m: 0
});

const descriptionStyle = css({
    m: 0,
    maxWidth: "48rem",
    color: "var(--colors-secondary)",
    textAlign: "left",
    mb: 2,

    md: {
        maxWidth: "44rem"
    }
});

const benefitsListStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2.5,
    width: "100%",
    maxWidth: "48rem",
    m: 0,
    p: 0,
    listStyle: "none",
    color: "var(--colors-secondary)",
    textAlign: "left",
    mb: 4,

    md: {
        maxWidth: "44rem",
        gap: 2
    }
});

const benefitItemStyle = css({
    display: "grid",
    gridTemplateColumns: "1.6rem minmax(0, 1fr)",
    gap: 1,
    alignItems: "start"
});

const benefitIconStyle = css({
    mt: "2px",
    color: "var(--colors-secondary)"
});

const benefitTextStyle = css({
    lineHeight: 1.35
});

const contactActionsStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    gap: 1.5
});

const primaryCtaStyle = css({
    width: "100%",
    minWidth: "20rem",
    maxWidth: "46.8rem",
    textTransform: "uppercase"
});
